import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { EVENTO_ANCORA, limparAncora } from '../Accordion/abrirPorAncora';

// Altura da barra, publicada no <html> enquanto ela está fixa. As margens de
// rolagem (Accordion/style.css e AnchorNav/style.css) descontam este valor: a
// barra quebra em mais de uma linha quando os links não cabem, então a altura
// não é uma constante.
export const VAR_ALTURA_DA_BARRA = '--ag-anchor-nav-h';

// Classe que o View põe na barra quando os links quebram em mais de uma linha.
// O CSS (acima de 768px) a usa para compactar o respiro vertical dos links; uma
// barra de uma linha não a recebe e fica como era.
export const CLASSE_MULTILINHA = 'ag-anchor-nav--multilinha';

// A variável é uma só no <html>. Cada barra que a publica fica anotada aqui,
// com o último valor que escreveu; ao sair, uma barra tira o próprio registro
// e a variável passa a valer o que a outra barra da página escreveu por último
// (ou sai do <html>, se não sobrou nenhuma). Sem isto, a saída de uma barra
// apagaria a altura da que continua na tela.
const publicadores = new Map();

const publicarAltura = (raiz, dono, valor) => {
  // sai e entra de novo: o último da fila é sempre quem escreveu por último
  publicadores.delete(dono);
  publicadores.set(dono, valor);
  raiz.style.setProperty(VAR_ALTURA_DA_BARRA, valor);
};

const retirarAltura = (raiz, dono) => {
  publicadores.delete(dono);
  const restantes = Array.from(publicadores.values());
  if (restantes.length > 0) {
    raiz.style.setProperty(
      VAR_ALTURA_DA_BARRA,
      restantes[restantes.length - 1],
    );
  } else {
    raiz.style.removeProperty(VAR_ALTURA_DA_BARRA);
  }
};

// Diferença (px) entre o centro de dois links, na vertical, a partir da qual
// eles estão em linhas diferentes. Na mesma linha a diferença é de meio pixel
// (a chamada é centralizada na linha); entre linhas é a altura de uma linha,
// no mínimo ~38px.
const TOLERANCIA_DE_LINHA = 10;

// Os links da barra passaram para mais de uma linha? Só olha a posição vertical
// dos links: a compactação mexe em padding vertical e em folga entre linhas, e
// nunca na largura de nada, então a resposta é a mesma com a classe ligada ou
// desligada (sem vaivém). Link sem tamanho (`display: none`, por exemplo) não
// ocupa linha nenhuma e fica de fora: o retângulo dele é todo zero, e contaria
// como uma linha à parte.
const estaQuebrada = (barra) => {
  const caixas = Array.from(barra.querySelectorAll('a'))
    .map((a) => a.getBoundingClientRect())
    .filter((r) => r.height !== 0 && r.width !== 0);
  if (caixas.length < 2) return false;
  const centro = (r) => r.top + r.height / 2;
  const primeiro = centro(caixas[0]);
  return caixas.some(
    (r) => Math.abs(centro(r) - primeiro) > TOLERANCIA_DE_LINHA,
  );
};

// A âncora do endereço (`#contato`), já decodificada, sem o `#`.
const ancoraDoEndereco = () => {
  try {
    return decodeURIComponent(window.location.hash.replace(/^#/, ''));
  } catch (e) {
    return '';
  }
};

// Margem de rolagem de um elemento, em px (`NaN` quando o navegador não diz).
const margemDeRolagem = (el) =>
  parseFloat(window.getComputedStyle(el).scrollMarginTop);

// Carga direta com `#ancora` no endereço: o navegador faz o salto nativo antes
// de o JS publicar a altura da barra, com a margem de uma linha (64px + folga),
// e uma barra de duas linhas cobre o alvo. Quando a altura chega, se o alvo
// ainda está exatamente onde aquele salto o deixou (o leitor não rolou), refaz
// o salto com a margem nova. Mudar a margem é o que dispara: barra de uma linha
// não mexe em nada. Item da sanfona (<details>) fica de fora: ela abre e rola
// por conta própria, também descontando a altura real da barra.
const refazerSaltoDoEndereco = (aplicar) => {
  const id = ancoraDoEndereco();
  const alvo = id ? document.getElementById(id) : null;
  if (!alvo || alvo.tagName === 'DETAILS') {
    aplicar();
    return;
  }
  // Lido antes de `aplicar`: compactar a barra encolhe a página e move o alvo,
  // mas a pergunta é se ele estava onde o salto do navegador o deixou.
  const margemAntes = margemDeRolagem(alvo);
  const topoAntes = alvo.getBoundingClientRect().top;
  aplicar();
  const margemDepois = margemDeRolagem(alvo);
  if (
    Number.isFinite(margemAntes) &&
    Number.isFinite(margemDepois) &&
    Math.abs(margemDepois - margemAntes) > 0.5 &&
    Math.abs(topoAntes - margemAntes) <= 1 &&
    typeof alvo.scrollIntoView === 'function'
  ) {
    alvo.scrollIntoView({ block: 'start', behavior: 'instant' });
  }
};

const AnchorNavView = ({ data }) => {
  const items = Array.isArray(data?.items) ? data.items : [];
  const align = data?.align || 'left';
  const sticky = data?.sticky !== false;
  const navRef = useRef(null);
  const [activeAnchor, setActiveAnchor] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined' || items.length === 0) return undefined;
    const anchors = items.map((it) => limparAncora(it?.anchor)).filter(Boolean);
    if (anchors.length === 0) return undefined;

    const targets = anchors
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (targets.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length > 0) {
          setActiveAnchor(visible[0].target.id);
        }
      },
      {
        rootMargin: '-20% 0px -60% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );
    targets.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, [items]);

  // Mantém em dia o que depende do tamanho real da barra: a classe
  // `ag-anchor-nav--multilinha` (quando os links quebram de linha) e a altura em
  // `--ag-anchor-nav-h` (no <html>), medida já com a barra compactada. A barra
  // muda de tamanho quando a janela muda de largura (os links quebram de linha),
  // quando a fonte da página termina de carregar e quando o texto do item muda
  // no editor. Só vale enquanto a barra está fixa, e quem diz isso é o CSS
  // (position: sticky; abaixo de 768px ela é static): é o mesmo teste da
  // rolagem da sanfona, e a largura de 768px não é repetida aqui. Barra que não
  // está fixa (a opção "fixa" desligada, ou o celular) não ganha classe nem
  // publica nada, e a margem de rolagem volta a ser a do CSS. A medida roda num
  // quadro de animação, não dentro do aviso do ResizeObserver: compactar muda a
  // altura da própria barra, e mudar o tamanho do que se observa dentro do
  // aviso faz o navegador acusar "ResizeObserver loop". Só no navegador (o
  // servidor não monta efeito) e, sem ResizeObserver, mede ao montar e a cada
  // `resize` da janela. Sai do <html> (e a classe, da barra) quando a barra sai.
  const temItens = items.length > 0;
  useEffect(() => {
    const barra = navRef.current;
    if (typeof window === 'undefined' || !temItens || !sticky || !barra) {
      return undefined;
    }
    const raiz = document.documentElement;
    const dono = Symbol('barra de âncoras');
    let primeira = true;
    const aplicar = () => {
      barra.classList.toggle(CLASSE_MULTILINHA, estaQuebrada(barra));
      // para cima: uma altura fracionária arredondada para baixo deixaria o
      // alvo uma fração de pixel por baixo da barra
      const altura = Math.ceil(barra.getBoundingClientRect().height);
      publicarAltura(raiz, dono, `${altura}px`);
    };
    const medir = () => {
      const fixa = /sticky$/.test(window.getComputedStyle(barra).position);
      if (!fixa) {
        // só escreve se há o que tirar: `remove` de uma classe ausente ainda
        // reescreve o atributo, a cada `resize` no celular
        if (barra.classList.contains(CLASSE_MULTILINHA)) {
          barra.classList.remove(CLASSE_MULTILINHA);
        }
        retirarAltura(raiz, dono);
        return;
      }
      if (primeira) {
        primeira = false;
        refazerSaltoDoEndereco(aplicar);
      } else {
        aplicar();
      }
    };
    let agendado = false;
    let quadro = 0;
    const agendar = () => {
      if (agendado) return;
      if (typeof window.requestAnimationFrame !== 'function') {
        medir();
        return;
      }
      agendado = true;
      quadro = window.requestAnimationFrame(() => {
        agendado = false;
        medir();
      });
    };
    medir();
    const observador =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(agendar);
    if (observador) observador.observe(barra);
    window.addEventListener('resize', agendar);
    return () => {
      window.removeEventListener('resize', agendar);
      if (observador) observador.disconnect();
      if (agendado && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(quadro);
      }
      barra.classList.remove(CLASSE_MULTILINHA);
      retirarAltura(raiz, dono);
    };
  }, [temItens, sticky]);

  const handleClick = (e, anchor) => {
    if (typeof window === 'undefined') return;
    const id = limparAncora(anchor);
    const target = id ? document.getElementById(id) : null;
    if (!target) return;
    e.preventDefault();
    // O alvo é o próprio item da sanfona (<details>): ela abre o item e rola até
    // ele, descontando a altura desta barra. Rolar aqui também seria rolar duas
    // vezes, e a primeira sem o item aberto.
    if (target.tagName !== 'DETAILS') {
      // Alvo dentro de um item fechado (um título no painel, por exemplo): a
      // sanfona só cuida dos itens dela, então abro aqui os de fora, de dentro
      // para fora, e só depois meço. Fora de qualquer <details>, o laço não faz
      // nada e a rolagem é a de sempre.
      for (
        let d = target.closest('details');
        d;
        d = d.parentElement?.closest('details')
      ) {
        d.open = true;
      }
      const navHeight = navRef.current ? navRef.current.offsetHeight : 0;
      const y =
        target.getBoundingClientRect().top + window.pageYOffset - navHeight - 8;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, '', `#${id}`);
    }
    // O replaceState não dispara `hashchange`: a sanfona é avisada por aqui.
    // Se nenhuma sanfona é dona do id, o aviso não faz nada.
    window.dispatchEvent(new CustomEvent(EVENTO_ANCORA, { detail: { id } }));
  };

  if (items.length === 0) return null;

  const cls = [
    'ag-anchor-nav',
    `ag-anchor-nav--${align}`,
    sticky ? 'ag-anchor-nav--sticky' : 'ag-anchor-nav--static',
  ].join(' ');

  return (
    <nav className={cls} aria-label="Navegação por seção" ref={navRef}>
      <div className="ag-anchor-nav__inner">
        {items.map((it, idx) => {
          const anchor = limparAncora(it?.anchor);
          const label = it?.label || '';
          const isActive = activeAnchor && anchor === activeAnchor;
          const linkClass =
            [isActive && 'is-active', it?.destaque && 'ag-anchor-nav__cta']
              .filter(Boolean)
              .join(' ') || undefined;
          return (
            <a
              key={`${anchor}-${idx}`}
              href={`#${anchor}`}
              className={linkClass}
              onClick={(e) => handleClick(e, anchor)}
            >
              {label}
            </a>
          );
        })}
      </div>
    </nav>
  );
};

AnchorNavView.propTypes = {
  data: PropTypes.shape({
    items: PropTypes.array,
    align: PropTypes.string,
    sticky: PropTypes.bool,
  }).isRequired,
};

export default AnchorNavView;
